import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useProject } from 'hooks/useProject';
import useProjectInfoProvider from 'hooks/useProjectInfoProvider';
import { handleFormatDate } from 'libs/mindUtils';
import { Link, Stack } from '@mui/material';

const GridItem = styled(Grid)(() => ({
  display: 'flex',
}));
const GridBox = styled(Box)(({ theme }) => ({
  padding: '8px 16px',
  textAlign: 'center',
  color: theme.palette.mind.black.A0,
  border: `1px solid ${theme.palette.divider}`,
  borderTop: 0,
  minHeight: 64,
  display: 'flex',
  alignItems: 'center',
  flex: 1,
}));
const ItemTitle = styled(GridBox)(({ theme }) => ({
  ...theme.typography.s13w800,
  '&:first-of-type': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  '&:last-of-type': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  justifyContent: 'flex-start',
}));

const ItemContent = styled(GridBox)(({ theme }) => ({
  ...theme.typography.s13w500,
  '&:first-of-type': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  '&:last-of-type': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

function ProjectInfoRow({ title, content, onClick }: { title: string; content: string; onClick?: () => void }) {
  return (
    <>
      <GridItem item xs={4}>
        <ItemTitle>{title}</ItemTitle>
      </GridItem>
      <GridItem item xs={8}>
        <ItemContent sx={{ borderLeft: 'none' }}>
          <Stack direction="row" justifyContent="space-between" sx={{ display: 'flex', width: '100%' }}>
            {content}
            {onClick && (
              <Link
                underline="always"
                sx={{ cursor: 'pointer', color: '#0F6CBD', typography: 's14w500' }}
                component="button"
                onClick={onClick}
              >
                수정
              </Link>
            )}
          </Stack>
        </ItemContent>
      </GridItem>
    </>
  );
}
interface IProjectResultInfoProps {
  type: 'inputs' | 'dcInputs';
}
function ProjectResultInfo({ type }: IProjectResultInfoProps): JSX.Element {
  const { inputs, dcInputs, onChange, onChangeDC } = useProjectInfoProvider();
  const conditionInputs = type === 'inputs' ? inputs : dcInputs;
  const conditionOnChange = type === 'inputs' ? onChange : onChangeDC;
  const { getProjectIdDetail } = useProject();
  const { data } = getProjectIdDetail(conditionInputs.projectId || '');
  const detail = useMemo(() => {
    return data || {};
  }, [data]);

  const handleAccountsListByComma = useMemo(() => {
    if (!detail?.projectAdmins || detail?.projectAdmins.length === 0) {
      return '-';
    }
    return detail?.projectAdmins?.map(obj => obj.name)?.join(', ');
  }, [detail?.projectAdmins]);

  const handleModifyPage = () => {
    conditionOnChange('activeStep', 0);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={0}>
        <ProjectInfoRow title="프로젝트 이름" content={detail?.projectName || '-'} />
        <ProjectInfoRow
          title="프로젝트 기간"
          content={`${handleFormatDate(detail?.projectStartAt || '')} ~ ${handleFormatDate(detail?.projectEndAt || '')}`}
          onClick={handleModifyPage}
        />
        <ProjectInfoRow title="기업명" content={detail?.compName || '-'} onClick={handleModifyPage} />
        <ProjectInfoRow title="프로젝트 담당자" content={handleAccountsListByComma} onClick={handleModifyPage} />
        <ProjectInfoRow
          title="프로젝트 참여 명단"
          content={detail?.projectPartUserFileName || '-'}
          onClick={handleModifyPage}
        />
        {type === 'inputs' && (
          <ProjectInfoRow
            title="진단도구"
            content={detail?.survey?.surveyName || '-'}
            onClick={() => {
              conditionOnChange('activeStep', 1);
            }}
          />
        )}
        {type === 'dcInputs' && (
          <ProjectInfoRow
            title="진단문항 파일"
            content={detail?.inspection?.saveFileName || '-'}
            onClick={handleModifyPage}
          />
        )}
      </Grid>
    </Box>
  );
}
export default ProjectResultInfo;
